import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getDefaultSubscriptionSelector } from '@modules/subscriptions/core/selectors';
import { updateSettings } from '../../settings/core/actions';
import { VISIBLE_RECIPES } from '@constants/subscription';
import { findFreshLine } from '@modules/subscriptions/utils/lineItems';

interface IUseSubscriptionHook {
  defaultSubscription: ISubscription | undefined;
  setDefaultSubscription: (subscriptions: ISubscription[]) => void;
  switchDefaultSubscription: (subscription: ISubscription) => void;
}

export const useSubscriptions = (): IUseSubscriptionHook => {
  const dispatch = useDispatch();

  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);

  const setDefaultSubscription = (subscriptions: ISubscription[]) => {
    if (!defaultSubscription) {
      let nextDefaultSubscription: ISubscription | null;

      if (subscriptions.length) {
        const firstActiveSubscription = subscriptions?.find((sub) => sub.status === 'ACTIVE');
        nextDefaultSubscription = firstActiveSubscription ?? subscriptions[0];
      } else {
        nextDefaultSubscription = null;
      }

      dispatch(updateSettings({ defaultSubscription: nextDefaultSubscription?.id ?? null }));
      updateSubscriptionOptions(nextDefaultSubscription);
    }
  };

  const switchDefaultSubscription = (subscription: ISubscription) => {
    dispatch(updateSettings({ defaultSubscription: subscription?.id }));
    updateSubscriptionOptions(subscription);
  };

  const updateSubscriptionOptions = (subscription: ISubscription | null) => {
    const subscriptionOptions = findFreshLine(subscription?.lines)?.product?.options;
    if (!subscriptionOptions) return;

    const subscriptionRecipes = VISIBLE_RECIPES.map((recipe) => recipe.id).filter((recipe) =>
      subscriptionOptions.recipes?.includes(recipe as string),
    );

    dispatch(
      updateSettings({
        subscriptionOptions: {
          recipes: subscriptionRecipes,
          cycle: subscriptionOptions?.cycle,
          pension: subscriptionOptions?.pension,
        },
      }),
    );
  };

  return {
    defaultSubscription,
    setDefaultSubscription,
    switchDefaultSubscription,
  };
};
